import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { usePrefetchRoles } from '#api/roles.queries.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { HostsCountLink } from '#components/patterns/entity-links/hosts-count-link/HostsCountLink.tsx';
import { MeatballMenu, MenuItem } from '#components/patterns/menus/meatball-menu/index.ts';
import { RoleName } from '#components/patterns/role-name/RoleName.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import { InlineTagsList } from '#components/patterns/tags/InlineTagsList.tsx';
import { allowedHostsFilter, hasAllowedRole } from '#screens/roles/detail/allowed-hosts-filter/allowedHostsFilter.ts';
import { stringifyPortRange } from '#screens/roles/detail/stringify-port-range/stringifyPortRange.ts';
import type { CIDRFirewallRuleFormData } from '#screens/routes/form/firewall-rule/cidr-firewall-rule-form-schema.ts';
import styles from './CIDRFirewallRulesList.module.css';

type Data = CIDRFirewallRuleFormData;

type Props = {
  firewallRules: Data[];
  onRemove?: (index: number) => void;
  onEdit?: (index: number) => void;
  onDuplicate?: (index: number) => void;
  className?: string;
  children?: React.ReactNode;
  hideOverflowX?: boolean;
  /**
   * Counts and links the hosts each rule allows to reach the route, within this network. A route
   * only accepts traffic from hosts in its router's network.
   */
  networkID?: string;
};

const columnHelper = createColumnHelper<Data>();

export function CIDRFirewallRulesList({
  firewallRules,
  hideOverflowX,
  className,
  children,
  onEdit,
  onRemove,
  onDuplicate,
  networkID,
}: Props) {
  usePrefetchRoles();

  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor('localCIDR', {
        header: 'CIDR',
        size: 160,
      }),
      columnHelper.display({
        header: 'Allowed hosts',
        // The count link never truncates, so it gets its own room rather than squeezing the role name.
        size: 320 + (networkID ? 60 : 0),
        cell(info) {
          const { allowedRoleID, tags } = info.row.original;

          const hasRole = hasAllowedRole(allowedRoleID);

          return (
            <div className="flex align-baseline nowrap gap-8">
              {hasRole ?
                <span className="flex gap-4 mw-0">
                  <RoleName className="inline truncate padr-0" roleID={allowedRoleID} />
                  {`hosts ${tags.length ? 'tagged ' : ''}`}
                </span>
              : `All hosts ${tags.length ? 'tagged ' : ''}`}
              {tags.length ?
                <InlineTagsList tagNames={tags} truncateAfter={1} />
              : null}
              {networkID ?
                <HostsCountLink filter={allowedHostsFilter(info.row.original, networkID)} />
              : null}
            </div>
          );
        },
      }),
      columnHelper.accessor('protocol', {
        header: 'Protocol',
        size: 80,
        cell(info) {
          const proto = info.getValue();
          return proto === 'ANY' ? 'Any' : proto;
        },
      }),
      columnHelper.accessor('portRange', {
        header: 'Port range',
        size: 100,
        cell(info) {
          const value = info.getValue();
          return stringifyPortRange(value);
        },
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        // The fill column: free text soaks up whatever width the sized columns leave over, and
        // gives it back first when space runs out; the size is only its floor.
        size: 150,
        meta: {
          truncate: true,
          fill: true,
        },
      }),
    ];
    if (onEdit && onRemove) {
      cols.push(
        columnHelper.display({
          id: 'action-menu',
          header: '',
          size: 34,
          cell(info) {
            const { index } = info.row;
            return (
              <div className="flex justify-end">
                <MeatballMenu>
                  <MenuItem onClick={() => onEdit(index)}>Edit</MenuItem>
                  {onDuplicate ?
                    <MenuItem onClick={() => onDuplicate(index)}>Duplicate</MenuItem>
                  : null}
                  <MenuItem onClick={() => onRemove(index)}>Delete</MenuItem>
                </MeatballMenu>
              </div>
            );
          },
        })
      );
    }
    return cols;
  }, [onEdit, onRemove, onDuplicate, networkID]);

  return (
    <div className={className}>
      {firewallRules.length ?
        <Table
          className={styles.CIDRFirewallRulesList}
          data={firewallRules}
          columns={columns}
          hideOverflowX={hideOverflowX}
          fixedLayout={true}
        />
      : <Banner className="mb-16">None defined</Banner>}
      {children}
    </div>
  );
}
